import {
  DownloadActions,
  GlobalPlayerStore,
  PlayerFileType,
} from "../../types";

export const createDownloadSlice = (store: GlobalPlayerStore): DownloadActions => {
  let abortController = new AbortController();
  let downloadedChunks: Uint8Array[] = [];

  return {
    startDownload: (
      url?: string, fileSize?: number, fileType: PlayerFileType = PlayerFileType.Video,
    ) => {
      const state = store.get();
      const downloadUrl = url || state.source;
      if (!downloadUrl) return;

      store.set({
        isDownloading: true,
        isPaused: false,
      });

      abortController = new AbortController();
      const signal = abortController.signal;
      downloadedChunks = [];

      fetch(downloadUrl, {
        headers: {
          Range: `bytes=${state.downloadedSize}-`,
        },
        signal,
      })
        .then((response) => {
          const reader = response.body?.getReader();
          let downloaded = state.downloadedSize;

          return new ReadableStream({
            start(controller) {
              function pump() {
                reader?.read().then(({
                  done,
                  value,
                }) => {
                  if (done) {
                    controller.close();
                    store.set({
                      isDownloading: false,
                    });

                    if (!signal.aborted) {
                      store.set({
                        isCompleted: true,
                      });

                      const blob = new Blob(downloadedChunks as BlobPart[], {
                        type: fileType,
                      });
                      const link = document.createElement("a");
                      link.href = URL.createObjectURL(blob);
                      link.download = downloadUrl.split("/").pop() || "file";
                      link.click();
                    }

                    return;
                  }

                  if (value) {
                    downloadedChunks.push(value);
                    downloaded += value.length;
                    const progress = (downloaded / (fileSize || 1)) * 100;

                    store.set({
                      downloadProgress: Math.round(progress),
                      downloadedSize: downloaded,
                    });

                    controller.enqueue(value);
                  }

                  if (!signal.aborted) pump();
                })
                  .catch((error) => {
                    if (error.name !== "AbortError") {
                      console.error("Pump error:", error);
                      controller.error(error);
                    }
                  });
              }

              pump();
            },
          });
        })
        .catch((e) => {
          if (e.name !== "AbortError") {
            store.set({
              isDownloading: false,
            });
          }
        });
    },

    pauseDownload: () => {
      abortController.abort();
      store.set({
        isPaused: true,
        isDownloading: false,
      });
    },

    cancelDownload: () => {
      abortController.abort();
      downloadedChunks = [];

      store.set({
        isDownloading: false,
        downloadedSize: 0,
        downloadProgress: 0,
        isPaused: false,
        isCompleted: false,
      });
    },

    completeDownload: () => {
      store.set({
        isCompleted: true,
        isDownloading: false,
      });
    },

    updateProgress: (progress, size) => {
      store.set({
        downloadProgress: progress,
        downloadedSize: size,
      });
    },
  };
};
